import { CommonModule } from '@angular/common';
import { Component, OnDestroy, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { IContents, IPresignedURLResponse } from '../../models';
import { Constants } from '../../common';
import { CommonService, ContentsService } from '../../services';
import { HttpErrorResponse, HttpEvent, HttpEventType, HttpResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { FileSizePipe } from '../file-size-pipe';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CommonFunctions } from '../../classes';

// Simple unique ID generator (for demo)
function generateUniqueId(): string {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

@Component({
    selector: 'app-file-upload-box',
    imports: [
        CommonModule,
        MatCardModule,
        MatIconModule,
        MatButtonModule,
        MatProgressBarModule,
        MatExpansionModule,
        MatTooltipModule,
        FileSizePipe
    ],
    templateUrl: './file-upload-box.component.html',
    styleUrl: './file-upload-box.component.scss'
})
export class FileUploadBoxComponent implements OnDestroy {

  @ViewChild('uploadPanel') uploadPanel!: MatExpansionPanel;


  // Map to store subscriptions keyed by unique file uploadKey
  subscriptions = new Map<string, Subscription[]>();

  files = [];

  constructor(private commonService: CommonService, private contentsService: ContentsService) {
    this.commonService.startUploadingFile.subscribe((content: IContents) => this.startUploadingContent(content));

    // Ensure existing files have uploadKey assigned (if any)
    this.commonService.filesToUpload?.forEach(file => {
      if (!file.uploadKey) {
        file.uploadKey = generateUniqueId();
      }
    });
  }

  ngOnDestroy() {
    this.closeUploadPanelAll();
  }

  getFiles() {
    return this.commonService.filesToUpload?.length > 0 ? this.commonService.filesToUpload : [];
  }

  getFileFormatIcon(file: IContents) {
    const file_format = file?.file_format ? `.${file.file_format}` : '';
    const mediaType = Constants.getMediaType(file_format);
    return 'svg/media_types/' + mediaType.toLowerCase() + '.svg';
  }

  getFilesCount() {
    return this.commonService?.filesToUpload?.length > 0 ? this.commonService.filesToUpload.length : 0;
  }

  // Generate or get stable unique key for content
  getContentKey(content: IContents): string {
    if (!content.uploadKey) {
      // Defensive: generate if missing
      content.uploadKey = generateUniqueId();
    }
    return content.uploadKey;
  }

  // Remove a single subscription from the array and map when it completes/errors
  private removeSubscription(key: string, subToRemove: Subscription) {
    const subs = this.subscriptions.get(key);
    if (!subs) return;
    const idx = subs.indexOf(subToRemove);
    if (idx > -1) {
      subs.splice(idx, 1);
    }
    if (subs.length === 0) {
      this.subscriptions.delete(key);
    }
  }

  retryUpload(content: IContents) {
    if (content.uploadFailed) {
      // console.log(`Retrying upload for: ${content.title}`);

      // Reset upload states
      content.uploading = false;
      content.uploadComplete = false;
      content.uploadFailed = false;
      content.progress = 0;
      content.uploadFailedMessage = '';

      // Retry Upload
      this.startUploadingContent(content);
    } else {
      // console.log(`Cannot retry: ${content.title} is not in a failed state.`);
    }
  }

  removeFile(file: File) {
    this.files = this.files.filter(f => f !== file);
  }

  startUploadingContent(content: IContents) {
    this.uploadPanel.open();
    const isAlreadyUploading = this.commonService.filesToUpload.some(
      x => x.title === content.title && x.file_size === content.file_size && x.uploading
    );
    if (isAlreadyUploading) {
      // console.warn(`File "${content.title}" is already uploading.`);
      return;
    }

    const getFileExtension = (filename: string) => filename.substring(filename.lastIndexOf('.')).toLowerCase();

    const extension = getFileExtension(content.file.name);
    const maxSize = this.commonService.maxFileSizes[extension];

    if (maxSize && content.file.size > maxSize) {
      this.handleUploadError(content, 'File exceeds max size limit');
      return;
    }

    if (!content.uploadComplete) {
      // Assign uploadKey if missing
      const key = this.getContentKey(content);

      if (!this.subscriptions.has(key)) {
        this.subscriptions.set(key, []);
      }

      // Capture metadata BEFORE asynchronous operations
      // Prefer per-content targetFolderId (set when dropping files onto a specific folder),
      // otherwise fall back to the currently navigated selectedFolder.
      const targetFolderIdStr = (content as any).targetFolderId !== undefined
        ? (content as any).targetFolderId.toString()
        : (this.commonService?.selectedFolder?.id?.toString() || '');

      const metadata = {
        title: content.title,
        file_format: content.file_format,
        file_size: content.file_size?.toString(),
        file_duration: content.file_duration?.toString(),
        resolution: content.resolution,
        folder: targetFolderIdStr,
        category: content.category,
        source: content.source
      };

      content.uploading = true;
      content.uploadComplete = false;
      content.uploadFailed = false;
      content.progress = 0;
      content.uploadFailedMessage = '';

      // Get presigned URL subscription
      const uploadContentType = CommonFunctions.resolveFileMimeType(content.file);
      const presignedUrlSub = this.contentsService.getPresignedURLForS3(content.file.name, uploadContentType, content.file_size)
        .subscribe({
          next: (presignedData: IPresignedURLResponse) => {
            if (!presignedData?.url) throw new Error('No presigned URL received');

            // Upload the file with progress observable subscription
            const uploadSub = this.contentsService.uploadFileToS3WithProgress(presignedData.url, content.file, uploadContentType)
              .subscribe({
                next: (event: HttpEvent<void>) => {
                  if (event.type === HttpEventType.UploadProgress) {
                    const progress = Math.round((100 * event.loaded) / (event.total || content.file_size));
                    content.progress = Math.min(progress, 99);
                  } else if (event instanceof HttpResponse) {
                    // On upload success, send metadata
                    const formData = new FormData();
                    Object.entries(metadata).forEach(([k, v]) => {
                      if (v) formData.append(k, v);
                    });
                    formData.append('thumbnail', '0');
                    formData.append('file_upload', presignedData.url.split('?')[0]);

                    const addContentsSub = this.contentsService.addContents(formData)
                      .subscribe({
                        next: (response) => {
                          content.progress = 100;

                          // 🔥 Emit newly added content
                          this.commonService.newContentAdded.next(response.data);

                          setTimeout(() => {
                            content.uploading = false;
                            content.uploadComplete = true;
                            this.cleanupUpload(content);
                          }, 100);
                        },
                        error: (metadataError: HttpErrorResponse) => {
                          this.handleUploadError(content, metadataError?.error?.message || 'Failed to save file metadata');
                        }
                      });
                    this.subscriptions.get(key)!.push(addContentsSub);

                    // Remove addContentsSub upon complete/error
                    addContentsSub.add(() => this.removeSubscription(key, addContentsSub));
                  }
                },
                error: (uploadError: HttpErrorResponse) => {
                  this.handleUploadError(content, uploadError.message || 'Unknown error');
                }
              });

            this.subscriptions.get(key)!.push(uploadSub);

            // Remove uploadSub upon complete/error
            uploadSub.add(() => this.removeSubscription(key, uploadSub));
          },
          error: (urlError: HttpErrorResponse) => {
            this.handleUploadError(content, urlError?.error?.message ? urlError.error.message : Constants.notifyMessages.commonErrorLoading );
          }
        });

      this.subscriptions.get(key)!.push(presignedUrlSub);

      // Remove presignedUrlSub upon complete/error
      presignedUrlSub.add(() => this.removeSubscription(key, presignedUrlSub));
    }
  }

  // Helper function for cleanup after completion
  private cleanupUpload(content: IContents) {
    this.commonService.filesToUpload = this.commonService.filesToUpload.filter(
      x => !(x.title === content.title && x.file_size === content.file_size)
    );
    this.commonService.refreshContents.next();

    // Unsubscribe and delete subscriptions map entry (if any remain)
    const key = this.getContentKey(content);
    if (this.subscriptions.has(key)) {
      const subs = this.subscriptions.get(key);
      subs?.forEach(sub => {
        if (sub && !sub.closed) {
          sub.unsubscribe();
        }
      });
      this.subscriptions.delete(key);
    }

    // console.log(`Cleanup done for ${key}, remaining uploads:`, this.commonService.filesToUpload.length);
  }

  private handleUploadError(content: IContents, message: string) {
    console.error('Upload error:', message);
    content.uploading = false;
    content.uploadFailed = true;
    content.uploadFailedMessage = message;
    content.progress = 0;

    // Similarly clean up subscriptions on error
    const key = this.getContentKey(content);
    if (this.subscriptions.has(key)) {
      const subs = this.subscriptions.get(key);
      subs?.forEach(sub => {
        if (sub && !sub.closed) sub.unsubscribe();
      });
      this.subscriptions.delete(key);
    }
  }

  // Close a single file upload panel and unsubscribe its subscriptions
  closeUploadPanel(content: IContents) {
    const key = this.getContentKey(content);
    const subs = this.subscriptions.get(key);
    if (subs) {
      subs.forEach(sub => {
        if (sub && !sub.closed) sub.unsubscribe();
      });
      this.subscriptions.delete(key);
    }
    this.commonService.filesToUpload = this.commonService.filesToUpload.filter(
      x => !(x.title === content.title && x.file_size === content.file_size)
    );

    // console.log(`Closed upload panel for ${key}`);
  }

  // Cancel ALL uploads and clear subscriptions
  closeUploadPanelAll() {
    this.subscriptions.forEach(subArr => {
      subArr.forEach(sub => {
        if (sub && !sub.closed) sub.unsubscribe();
      });
    });
    this.subscriptions.clear();
    this.commonService.filesToUpload = [];

    // console.log('Closed all upload panels');
  }
}
